import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogContent, DialogTitle, Typography, Chip } from '@mui/material';
import { ReactFlowProvider, ReactFlow, Node, Edge, Connection, NodeChange, EdgeChange, OnNodesChange, OnEdgesChange, OnConnect, addEdge, applyNodeChanges, applyEdgeChanges, XYPosition, MarkerType, Handle, Position, ConnectionMode } from 'reactflow';
import 'reactflow/dist/style.css';
import { equipment } from '../types/BatchConfigType';
import SaveCancelButtons from '../../../core-components/theme/components/SaveCancelComponent/SaveCancelButtons';
import { Primary } from '../../../core-components/theme/color-palette/colors';

interface BatchFlowDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { nodes: Node[]; edges: Edge[] }) => void;
  selectedEquipments: equipment[];
  initialData?: {
    nodes?: Node[];
    edges?: Edge[];
  };
}

const EquipmentNode: React.FC<any> = ({ data, selected }) => {
  return (
    <Box
      sx={{
        width: '120px',
        height: '60px',
        backgroundColor: '#E3F2FD',
        border: `2px solid ${selected ? Primary[400] : Primary[200]}`,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px',
        position: 'relative',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="target-1"
        style={{
          background: Primary[200],
          border: `1px solid ${Primary[500]}`,
          width: '8px',
          height: '8px',
          top: '25px',
          left: '-6px',
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="target-2"
        style={{
          background: Primary[200],
          border: `1px solid ${Primary[500]}`,
          width: '8px',
          height: '8px',
          top: '50px',
          left: '-6px',
        }}
      />
      
      <Handle
        type="source"
        position={Position.Right}
        id="source-1"
        style={{
          background: Primary[200],
          border: `1px solid ${Primary[500]}`,
          width: '8px',
          height: '8px',
          top: '25px',
          right: '-6px',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source-2"
        style={{
          background: Primary[200],
          border: `1px solid ${Primary[500]}`,
          width: '8px',
          height: '8px',
          top: '50px',
          right: '-6px',
        }}
      />
      
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          textAlign: 'center',
          color: Primary[900],
          lineHeight: 1.2,
        }}
      >
        {data.label}
      </Typography>
    </Box>
  );
};

const EquipmentNodeSelector: React.FC<{
  availableEquipments: equipment[];
  onEquipmentSelect: (equipment: equipment) => void;
}> = ({ availableEquipments, onEquipmentSelect }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, borderBottom: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" sx={{ color: '#666' }}>
        {t('batchConfig.availableEquipment', 'Available Equipment:')}
      </Typography>
      {availableEquipments.map((equipment) => (
        <Chip
          key={equipment.equipmentId}
          label={equipment.equipmentName}
          onClick={() => onEquipmentSelect(equipment)}
          sx={{
            backgroundColor: '#E3F2FD',
            color: '#1976D2',
            border: '1px solidrgb(163, 203, 236)',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#BBDEFB',
            },
          }}
        />
      ))}
      {availableEquipments.length === 0 && (
        <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
          {t('batchConfig.allEquipmentPlaced', 'All equipment has been placed in the flow')}
        </Typography>
      )}
    </Box>
  );
};

const BatchFlowCanvas: React.FC<{
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onEdgeDelete: (edgeId: string) => void;
}> = ({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onDragOver, onDrop, onEdgeDelete }) => {
  const nodeTypes = useMemo(() => ({
    equipment: EquipmentNode,
  }), []);

  return (
    <Box
      sx={{
        height: '400px',
        width: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <ReactFlowProvider>
        <Box sx={{ height: '100%', width: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2, minZoom: 0.5, maxZoom: 1.5 }}
            defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
            proOptions={{ hideAttribution: true }}
            connectionMode={ConnectionMode.Loose}
            onEdgeClick={(event, edge) => {
              onEdgeDelete(edge.id);
            }}
            defaultEdgeOptions={{
              type: 'default',
              animated: false,
              style: { stroke: Primary[200], strokeWidth: 2, cursor: 'pointer' },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 10,
                height: 10,
                color: Primary[200],
              },
            }}
          />
        </Box>
      </ReactFlowProvider>
    </Box>
  );
};

const BatchFlowDialog: React.FC<BatchFlowDialogProps> = ({
  open,
  onClose,
  onSubmit,
  selectedEquipments,
  initialData,
}) => {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState<Node[]>(initialData?.nodes || []);
  const [edges, setEdges] = useState<Edge[]>(initialData?.edges || []);

  const availableEquipments = useMemo(() => {
    const placedEquipmentIds = new Set(nodes.map(node => node.data.equipmentId));
    return selectedEquipments.filter(equipment => !placedEquipmentIds.has(equipment.equipmentId));
  }, [selectedEquipments, nodes]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    []
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    []
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target && connection.sourceHandle && connection.targetHandle) {
        const newEdge: Edge = {
          id: `${connection.source}-${connection.sourceHandle}-${connection.target}-${connection.targetHandle}`,
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          type: 'default',
          animated: false,
          style: { stroke: Primary[200], strokeWidth: 2, cursor: 'pointer' },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 10,
            height: 10,
            color: Primary[200],
          },
        };
        setEdges((eds) => addEdge(newEdge, eds));
      }
    },
    []
  );

  const handleEdgeDelete = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter(edge => edge.id !== edgeId));
  }, []);

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const equipmentData = event.dataTransfer.getData('application/equipment');
      if (!equipmentData) return;

      const equipment: equipment = JSON.parse(equipmentData);
      const position: XYPosition = {
        x: event.clientX - 200,
        y: event.clientY - 100,
      };

      const newNode: Node = {
        id: `equipment-${equipment.equipmentId}`,
        type: 'equipment',
        position,
        data: {
          label: equipment.equipmentName,
          equipmentId: equipment.equipmentId,
        },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    []
  );

  const handleEquipmentSelect = useCallback((equipment: equipment) => {
    const position: XYPosition = {
      x: Math.random() * 300 + 50,
      y: Math.random() * 200 + 50,
    };

    const newNode: Node = {
      id: `equipment-${equipment.equipmentId}`,
      type: 'equipment',
      position,
      data: {
        label: equipment.equipmentName,
        equipmentId: equipment.equipmentId,
      },
    };

    setNodes((nds) => [...nds, newNode]);
  }, []);

  const isAllEquipmentConfigured = useMemo(() => {
    return selectedEquipments.length === nodes.length;
  }, [selectedEquipments.length, nodes.length]);

  const handleSubmit = useCallback(() => {
    if (isAllEquipmentConfigured) {
      onSubmit({ nodes, edges });
      onClose();
    }
  }, [nodes, edges, onSubmit, onClose, isAllEquipmentConfigured]);

  const handleClose = useCallback(() => {
    setNodes(initialData?.nodes || []);
    setEdges(initialData?.edges || []);
    onClose();
  }, [initialData, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ style: { borderRadius: 20, width: '100%' } }}
    >
      <DialogTitle>{t('batchConfig.batchFlowConfiguration', 'Batch Flow Configuration')}</DialogTitle>
             <DialogContent>
         <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
           {t('batchConfig.batchFlowInstructions', 'Click on equipment chips below to add them to the flow, then connect them by dragging from one node to another. Each node has 2 input handles (left side) and 2 output handles (right side). Click on any connection line to delete it.')}
         </Typography>
        
        <EquipmentNodeSelector
          availableEquipments={availableEquipments}
          onEquipmentSelect={handleEquipmentSelect}
        />
        
                 <Box sx={{ mt: 2 }}>
           <BatchFlowCanvas
             nodes={nodes}
             edges={edges}
             onNodesChange={onNodesChange}
             onEdgesChange={onEdgesChange}
             onConnect={onConnect}
             onDragOver={onDragOver}
             onDrop={onDrop}
             onEdgeDelete={handleEdgeDelete}
           />
         </Box>
      </DialogContent>
             <Box sx={{mb:5, mr:5}}>
         <SaveCancelButtons
           saveValue={isAllEquipmentConfigured ? t('batchConfig.saveFlow', 'SAVE FLOW') : t('batchConfig.saveFlowWithCount', 'SAVE FLOW ({{current}}/{{total}} Equipment)').replace('{{current}}', String(nodes.length)).replace('{{total}}', String(selectedEquipments.length))}
           onSave={handleSubmit}
           onCancel={handleClose}
           cancelButtonStyle={{
            width: { xs: '50%', sm: '50%', md: '12%' },
            height: "100%",
            maxHeight: "55px",
            marginLeft: 5
          }}
          saveButtonStyle={{
            width: { xs: '50%', sm: '50%', md: '12%' },
            height: "100%",
            maxHeight: "55px"
          }}
           customSaveDisabled={!isAllEquipmentConfigured}
         />
       </Box>
    </Dialog>
  );
};

export default BatchFlowDialog;
